import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { FieldErrors, UseFormReturn } from 'react-hook-form'
import type {
    CareLogFormInput,
    CareLogFormValues,
} from '@/lib/schemas/care-log-schemas'
import { m } from '@/lib/paraglide/messages'

/** Metadata cho mỗi bước của wizard tạo/sửa nhật ký chăm sóc. */
export interface WizardStepMeta {
    key: 'place' | 'work' | 'verify'
    label: () => string
}

/** 3 bước theo mô hình hệ thống cũ (Hướng 2 — gộp field vào bước, không header). */
export const CARE_LOG_STEPS: WizardStepMeta[] = [
    { key: 'place', label: () => m.care_log_step_place() },
    { key: 'work', label: () => m.care_log_step_work() },
    { key: 'verify', label: () => m.care_log_step_verify() },
]

/**
 * Field nào nằm ở bước nào. Nút "Lưu nhật ký" chỉ có ở bước cuối, trong khi mỗi
 * bước chỉ render field của riêng nó — field lỗi ở bước trước ĐÃ UNMOUNT nên
 * không có chỗ nào hiện lỗi ra. Bảng này để `onInvalid` kéo người dùng về đúng
 * bước đang thiếu thay vì để màn hình đứng im.
 */
const FIELD_STEP: Partial<Record<keyof CareLogFormInput, number>> = {
    grower_id: 0,
    log_date: 0,
    parcels: 0,
    activity_type: 1,
    materials: 1,
    quantity: 1,
    implementer_ids: 1,
    note: 2,
    file_ids: 2,
}

/** Bước sớm nhất đang có lỗi; null khi không map được field nào. */
function firstErrorStep(errors: FieldErrors<CareLogFormInput>): number | null {
    let earliest: number | null = null
    for (const name of Object.keys(errors) as Array<keyof CareLogFormInput>) {
        const s = FIELD_STEP[name]
        if (s === undefined) continue
        if (earliest === null || s < earliest) earliest = s
    }
    return earliest
}

interface WizardParams {
    onInvalidStep?: () => void
    form: UseFormReturn<CareLogFormInput, object, CareLogFormValues>
    needsMaterials: boolean
    needsQuantity: boolean
}

export interface CareLogWizard {
    step: number
    total: number
    steps: WizardStepMeta[]
    isFirst: boolean
    isLast: boolean
    /** Validate bước hiện tại; nếu qua thì sang bước sau (không disable nút). */
    next: () => Promise<void>
    /** Lùi 1 bước (không validate). */
    back: () => void
    /** Nhảy tới bước — chỉ cho lùi về bước đã qua; tiến phải bấm "Tiếp theo". */
    goTo: (target: number) => void
    /**
     * Handler `onInvalid` cho `form.handleSubmit`. Không có nó thì bấm "Lưu"
     * lúc form sai là IM LẶNG TUYỆT ĐỐI (RHF không gọi callback và không làm gì
     * khác), còn ô báo đỏ thì nằm ở bước đã unmount. Đưa người dùng về bước sớm
     * nhất đang lỗi + toast + focus field đầu tiên.
     */
    onInvalid: (errors: FieldErrors<CareLogFormInput>) => void
}

/**
 * State-machine điều hướng wizard nhật ký chăm sóc. Đặt trong container
 * (useCareLogEditContext) để `step` sống sót khi đổi layout desktop⇄mobile lúc
 * resize. Validate theo bước dùng `form.trigger` (chỉ field bắt buộc của bước đó);
 * KHÔNG disable nút — bấm "Tiếp theo" khi thiếu field sẽ hiện lỗi và giữ nguyên
 * bước, đúng quy ước form của repo.
 */
export function useCareLogWizard({
    form,
    needsMaterials,
    needsQuantity,
    onInvalidStep,
}: WizardParams): CareLogWizard {
    const [step, setStep] = useState(0)
    const total = CARE_LOG_STEPS.length

    const validateStep = useCallback(
        async (s: number): Promise<boolean> => {
            if (s === 0) {
                // Bước Địa điểm: hộ trồng chè bắt buộc (ngày đã có mặc định) và
                // vùng trồng — bắt buộc hay không do schema quyết theo hộ đang
                // chọn (hộ chưa khai vùng nào thì bỏ trống được).
                return form.trigger(['grower_id', 'log_date', 'parcels'])
            }
            if (s === 1) {
                // Bước Công việc: loại hoạt động + field phụ thuộc hoạt động.
                // Cách ly KHÔNG nằm ở đây — nó không còn ràng buộc nào để kiểm.
                const fields: Array<keyof CareLogFormInput> = ['activity_type']
                if (needsMaterials) fields.push('materials')
                if (needsQuantity) fields.push('quantity')
                return form.trigger(fields)
            }
            return true // Bước Xác minh: không có field bắt buộc.
        },
        [form, needsMaterials, needsQuantity],
    )

    const next = useCallback(async () => {
        if (await validateStep(step)) {
            setStep((s) => Math.min(s + 1, total - 1))
        } else {
            onInvalidStep?.()
        }
    }, [step, validateStep, total, onInvalidStep])

    const back = useCallback(() => setStep((s) => Math.max(s - 1, 0)), [])

    const goTo = useCallback(
        (target: number) => setStep((s) => (target <= s ? target : s)),
        [],
    )

    // Field cần focus sau khi đã nhảy về bước chứa nó. Phải đợi một lượt render
    // vì lúc `onInvalid` chạy, field đó còn chưa mount.
    const pendingFocus = useRef<keyof CareLogFormInput | null>(null)
    useEffect(() => {
        const name = pendingFocus.current
        if (!name) return
        pendingFocus.current = null
        form.setFocus(name, { shouldSelect: true })
    }, [step, form])

    const onInvalid = useCallback(
        (errors: FieldErrors<CareLogFormInput>) => {
            toast.error(m.care_log_form_invalid())
            const target = firstErrorStep(errors)
            if (target === null) return
            // Field ở bước đang đứng đã mount → `shouldFocusError` của RHF lo
            // rồi; chỉ hẹn focus cho field sắp mount ở bước khác.
            if (target !== step) {
                pendingFocus.current =
                    (Object.keys(errors) as Array<keyof CareLogFormInput>).find(
                        (n) => FIELD_STEP[n] === target,
                    ) ?? null
            }
            // Nhảy THẲNG tới bước lỗi, không qua `goTo` (chỉ cho lùi) — lỗi có
            // thể ở bước sau nếu người dùng đã lùi lại rồi bấm Lưu.
            setStep(target)
        },
        [step],
    )

    return {
        step,
        total,
        steps: CARE_LOG_STEPS,
        isFirst: step === 0,
        isLast: step === total - 1,
        next,
        back,
        goTo,
        onInvalid,
    }
}
